"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

/**
 * Pauses every CSS animation inside a section while that section is off
 * screen (globals.css: [data-offscreen] … animation-play-state: paused).
 * Looping animations far down the page otherwise keep the browser busy on
 * every frame, which is what makes a long page feel heavy while scrolling.
 * Sections pick up exactly where they stopped when they come back into view.
 *
 * Watches every <section> and anything marked data-motion. Mounted once in
 * the root layout; re-scans after each navigation and when content changes.
 */
export function MotionGuard() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) e.target.removeAttribute("data-offscreen");
          else e.target.setAttribute("data-offscreen", "");
        }
      },
      // start a little before a section scrolls in, so nothing visibly jumps
      { rootMargin: "300px 0px 300px 0px" },
    );
    const seen = new WeakSet<Element>();
    const scan = () => {
      try {
        document.querySelectorAll("section, [data-motion]").forEach((el) => {
          if (seen.has(el)) return;
          seen.add(el);
          io.observe(el);
        });
      } catch {
        /* never let this break a page */
      }
    };
    scan();
    // pages load parts of their content after the first paint
    let t: ReturnType<typeof setTimeout> | undefined;
    const mo = new MutationObserver(() => {
      clearTimeout(t);
      t = setTimeout(scan, 250);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearTimeout(t);
      mo.disconnect();
      io.disconnect();
    };
  }, [pathname]);

  return null;
}
